interface HasUuid {
  uuid: string;
}

/**
 * The order `count` items end up in when the ones at `movingIndices` are lifted out and dropped as
 * a single block onto `toIndex`: after the target when dragging down, before it when dragging up —
 * which is how a lone dragged row behaves in a sortable list. The result lists original indices in
 * their new order.
 *
 * Returns undefined when the drop is a no-op (unknown rows, or dropping onto a row being dragged).
 */
export const reorderIndices = (
  count: number,
  fromIndex: number,
  toIndex: number,
  movingIndices: Array<number>,
): Array<number> | undefined => {
  const isOutOfRange = (index: number) => index < 0 || index >= count;
  if (isOutOfRange(fromIndex) || isOutOfRange(toIndex)) return undefined;

  const moving = new Set(movingIndices);
  if (moving.has(toIndex) || moving.size === 0) return undefined;

  const remaining: Array<number> = [];
  const moved: Array<number> = [];
  for (let index = 0; index < count; index += 1) {
    (moving.has(index) ? moved : remaining).push(index);
  }

  const insertAt = remaining.indexOf(toIndex) + (fromIndex < toIndex ? 1 : 0);
  return [...remaining.slice(0, insertAt), ...moved, ...remaining.slice(insertAt)];
};

/**
 * Drops the rows in `movingUuids` onto `overUuid` as a single block, keeping their relative order —
 * {@link reorderIndices} keyed by row rather than by position. Returns undefined for a no-op drop.
 */
export const reorderLines = <T extends HasUuid>(
  lines: Array<T>,
  activeUuid: string,
  overUuid: string,
  movingUuids: Array<string> = [activeUuid],
): Array<T> | undefined => {
  const indexByUuid = new Map(lines.map((line, index) => [line.uuid, index]));
  const indexOf = (uuid: string) => indexByUuid.get(uuid) ?? -1;

  const movingIndices = movingUuids.map(indexOf).filter((index) => index !== -1);
  const order = reorderIndices(lines.length, indexOf(activeUuid), indexOf(overUuid), movingIndices);

  return order?.map((index) => lines[index]);
};
